import React, { useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { toast } from "react-toastify";
import { isValidGstNumber } from "../utils/gstValidation";
import { customerMobileOf, formatAmount } from "../utils/commonUtils";

// Vendor style TAX INVOICE popup for an order. The title is editable in
// place (click and type) and Download saves the invoice as a PDF that
// looks exactly like the popup, including any edited title
const ones = [
  "",
  "ONE",
  "TWO",
  "THREE",
  "FOUR",
  "FIVE",
  "SIX",
  "SEVEN",
  "EIGHT",
  "NINE",
  "TEN",
  "ELEVEN",
  "TWELVE",
  "THIRTEEN",
  "FOURTEEN",
  "FIFTEEN",
  "SIXTEEN",
  "SEVENTEEN",
  "EIGHTEEN",
  "NINETEEN",
];
const tens = [
  "",
  "",
  "TWENTY",
  "THIRTY",
  "FORTY",
  "FIFTY",
  "SIXTY",
  "SEVENTY",
  "EIGHTY",
  "NINETY",
];

const twoDigitWords = (n) =>
  n < 20
    ? ones[n]
    : tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");

const threeDigitWords = (n) =>
  n >= 100
    ? ones[Math.floor(n / 100)] +
      " HUNDRED" +
      (n % 100 ? " AND " + twoDigitWords(n % 100) : "")
    : twoDigitWords(n);

// Indian numbering system: crore / lakh / thousand
const amountInWords = (num) => {
  num = Math.round(Math.abs(Number(num) || 0));
  if (num === 0) return "ZERO RUPEES ONLY";
  const words = [];
  const crore = Math.floor(num / 10000000);
  num %= 10000000;
  const lakh = Math.floor(num / 100000);
  num %= 100000;
  const thousand = Math.floor(num / 1000);
  num %= 1000;
  if (crore) words.push(threeDigitWords(crore) + " CRORE");
  if (lakh) words.push(twoDigitWords(lakh) + " LAKH");
  if (thousand) words.push(twoDigitWords(thousand) + " THOUSAND");
  if (num) words.push(threeDigitWords(num));
  return words.join(" ") + " RUPEES ONLY";
};

const monthNames = [
  "JANUARY",
  "FEBRUARY",
  "MARCH",
  "APRIL",
  "MAY",
  "JUNE",
  "JULY",
  "AUGUST",
  "SEPTEMBER",
  "OCTOBER",
  "NOVEMBER",
  "DECEMBER",
];

const formatInvoiceDate = (dateValue) => {
  const d = new Date(dateValue);
  if (isNaN(d.getTime())) return "";
  return `${String(d.getDate()).padStart(2, "0")}-${
    monthNames[d.getMonth()]
  }-${d.getFullYear()}`;
};

const formatNum = (n) =>
  Number.isInteger(Number(n)) ? String(Number(n)) : Number(n).toFixed(2);

const cellStyle = {
  border: "0.5px solid #000",
  padding: "0.3rem 0.45rem",
  verticalAlign: "top",
  textAlign: "left",
};

const boldCell = { ...cellStyle, fontWeight: "bold" };

const InvoiceModal = ({ show, onHide, order }) => {
  const { userInfo } = useSelector((state) => state.auth);
  const invoiceRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  // Outlet ("FROM") - matched by the order's outlet, falling back to the
  // currently selected outlet
  const orderOutletId = String(order?.outletId?._id || order?.outletId || "");
  const outlet =
    userInfo?.outletId?.find((ot) => ot._id === orderOutletId) ||
    userInfo?.outletId?.find((ot) => ot._id === userInfo?.selectedOutletId) ||
    userInfo?.outletId?.[0];

  // Customer ("TO"): the order outlet's customer record for the order's
  // mobile number (the same person can be a customer of several outlets)
  const customer = userInfo?.customersList?.find(
    (c) =>
      customerMobileOf(c) === String(order?.mobileNumber) &&
      (!orderOutletId || String(c.outletId) === orderOutletId)
  );

  // GST is only shown on the bill when the outlet has a genuine
  // 15-character GST number — orders already carry gstAmount: 0 in that
  // case, so this only controls whether the column/total are displayed.
  const gstApplicable = isValidGstNumber(outlet?.outletGstNumber);

  const outletMobiles = [outlet?.outletContact, outlet?.secondaryContact?.[0]]
    .filter(Boolean)
    .join(", ");
  const outletEmails = outlet?.outletEmails?.slice(0, 2).join(", ");

  const customerMobiles = [
    customerMobileOf(customer),
    customer?.secondaryMobiles?.[0],
  ]
    .filter(Boolean)
    .join(", ");
  const customerEmails = customer?.customerEmails?.slice(0, 2).join(", ");

  const hsnFromFoodItemId = (foodItemId) => {
    const idStr = String(foodItemId?._id || foodItemId || "");
    const outletIdStr = orderOutletId || String(outlet?._id || "");
    return outletIdStr && idStr.startsWith(outletIdStr)
      ? idStr.slice(outletIdStr.length)
      : idStr;
  };

  // Item rows straight from orderDetails:
  // MRP = sellingPrice, RATE = sellingPriceAfterOffers,
  // Total Value = RATE * QTY, gst columns from gstAmount.
  // GST is always shown item wise, irrespective of each item's gst %
  const invoiceItems = (order?.orderDetails || []).map((item) => {
    const qty = Number(item?.itemQuantity) || 0;
    const mrp = Number(item?.sellingPrice) || 0;
    const rate =
      Number(item?.sellingPriceAfterOffers ?? item?.sellingPrice) || 0;
    const gstPerUnit = Number(item?.gstAmount) || 0;

    return {
      hsnCode: hsnFromFoodItemId(item?.foodItemId),
      description: item?.itemName || "",
      mrp,
      qty,
      rate,
      totalValue: rate * qty,
      gstTotal: gstPerUnit * qty,
      totalWithGst: (rate + gstPerUnit) * qty,
    };
  });

  const invoiceTotal = invoiceItems.reduce((sum, it) => sum + it.totalValue, 0);
  const gstColumnTotal = invoiceItems.reduce((sum, it) => sum + it.gstTotal, 0);
  const totalWithGstSum = invoiceItems.reduce(
    (sum, it) => sum + it.totalWithGst,
    0
  );

  const rawGrandTotal = totalWithGstSum;
  const grandTotal = Math.round(rawGrandTotal);
  const roundOff = grandTotal - rawGrandTotal;

  const handleDownload = async () => {
    try {
      setDownloading(true);
      // Higher capture scale keeps the border lines thin and crisp when
      // the image is scaled down to fit the A4 page
      const canvas = await html2canvas(invoiceRef.current, {
        scale: 3,
        backgroundColor: "#ffffff",
        // Render against a virtual window wider than the fixed 760px
        // invoice so mobile viewports never clip or reflow the capture
        windowWidth: 820,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      let imgWidth = 190;
      let imgHeight = (canvas.height * imgWidth) / canvas.width;
      const maxHeight = 277;
      if (imgHeight > maxHeight) {
        imgWidth = (imgWidth * maxHeight) / imgHeight;
        imgHeight = maxHeight;
      }
      pdf.addImage(
        imgData,
        "PNG",
        (210 - imgWidth) / 2,
        10,
        imgWidth,
        imgHeight
      );
      pdf.save(`Invoice_${order?.orderId || "order"}.pdf`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to download invoice");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      fullscreen="sm-down"
      scrollable
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1rem" }}>
          Invoice - Order {order?.orderId}{" "}
          <small style={{ color: "grey", fontWeight: "normal" }}>
            (click the invoice title or amount in words to edit them)
          </small>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ backgroundColor: "#f2f2f2" }}>
        {/* The invoice always renders at a fixed 760px width so the layout
            (and the downloaded PDF) is identical on mobile, tablet and
            laptop. Smaller screens scroll it horizontally */}
        <div style={{ overflowX: "auto" }}>
          <div
            ref={invoiceRef}
            style={{
              backgroundColor: "#ffffff",
              color: "#000",
              border: "1px solid #000",
              fontFamily: "Arial, Helvetica, sans-serif",
              fontSize: "0.85rem",
              margin: "0 auto",
              width: "760px",
              minWidth: "760px",
              textAlign: "left",
            }}
          >
            {/* Editable invoice title */}
            <div
              contentEditable
              suppressContentEditableWarning
              style={{
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "1.05rem",
                padding: "0.45rem",
                borderBottom: "0.5px solid #000",
                outline: "none",
              }}
              title="Click to edit the invoice title"
            >
              TAX INVOICE
            </div>

            {/* FROM / TO + invoice number / date */}
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr>
                  <td style={{ ...cellStyle, width: "50%" }}>
                    <strong>FROM</strong>
                    <div style={{ marginTop: "0.4rem" }}>
                      <div>{outlet?.outletName?.toUpperCase()}</div>
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        {outlet?.outletAddress}
                      </div>
                      {outlet?.outletState && (
                        <div>{outlet.outletState.toUpperCase()}.</div>
                      )}
                      {outlet?.outletGstNumber && (
                        <div>GST IN : {outlet.outletGstNumber}</div>
                      )}
                      {outletEmails && <div>Email: {outletEmails}</div>}
                      {outletMobiles && <div>Mobile: {outletMobiles}</div>}
                    </div>
                  </td>
                  <td style={{ ...cellStyle, width: "50%" }}>
                    <strong>TO</strong>
                    <div style={{ marginTop: "0.4rem" }}>
                      {customer ? (
                        <>
                          <div>{customer?.customerName?.toUpperCase()}</div>
                          <div style={{ whiteSpace: "pre-wrap" }}>
                            {customer?.customerAddress}
                          </div>
                          {customer?.customerGst && (
                            <div>GSTIN:{customer.customerGst}</div>
                          )}
                          {customerEmails && <div>Email: {customerEmails}</div>}
                          {customerMobiles && (
                            <div>Mobile: {customerMobiles}</div>
                          )}
                        </>
                      ) : (
                        <div>{order?.mobileNumber}</div>
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td style={cellStyle}>
                    INVOICE NUMBER : {order?.invoiceNumber?.slice(10)}
                  </td>
                  <td style={cellStyle}>
                    DATE :{" "}
                    {formatInvoiceDate(order?.orderDate || order?.createdAt)}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Items + totals */}
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={cellStyle}>S.No</th>
                  <th style={cellStyle}>HSN Code</th>
                  <th style={{ ...cellStyle, width: "22%" }}>
                    Description of the goods
                  </th>
                  <th style={cellStyle}>MRP RATE</th>
                  <th style={cellStyle}>QTY</th>
                  <th style={cellStyle}>Rate(per item)</th>
                  <th style={cellStyle}>Total Value</th>
                  {gstApplicable && <th style={cellStyle}>GST</th>}
                  <th style={cellStyle}>Total</th>
                </tr>
              </thead>
              <tbody>
                {invoiceItems.map((item, index) => (
                  <tr key={index}>
                    <td style={cellStyle}>{index + 1}</td>
                    <td style={cellStyle}>{item.hsnCode}</td>
                    <td style={cellStyle}>{item.description?.toUpperCase()}</td>
                    <td style={cellStyle}>{formatAmount(item.mrp)}</td>
                    <td style={cellStyle}>{formatNum(item.qty)}</td>
                    <td style={cellStyle}>{formatAmount(item.rate)}</td>
                    <td style={cellStyle}>{formatAmount(item.totalValue)}</td>
                    {gstApplicable && (
                      <td style={cellStyle}>{formatAmount(item.gstTotal)}</td>
                    )}
                    <td style={cellStyle}>{formatAmount(item.totalWithGst)}</td>
                  </tr>
                ))}

                <tr>
                  <td style={cellStyle}></td>
                  <td style={cellStyle}></td>
                  <td style={boldCell}>AMOUNT IN WORDS</td>
                  <td style={cellStyle}></td>
                  <td style={cellStyle}></td>
                  <td style={boldCell}>Total</td>
                  <td style={boldCell}>{formatAmount(invoiceTotal)}</td>
                  {gstApplicable && (
                    <td style={boldCell}>{formatAmount(gstColumnTotal)}</td>
                  )}
                  <td style={boldCell}>{formatAmount(totalWithGstSum)}</td>
                </tr>
                <tr>
                  <td style={cellStyle}></td>
                  <td style={cellStyle}></td>
                  <td
                    rowSpan={2}
                    contentEditable
                    suppressContentEditableWarning
                    title="Click to edit the amount in words"
                    style={{
                      ...cellStyle,
                      fontSize: "0.7rem",
                      outline: "none",
                    }}
                  >
                    {amountInWords(grandTotal)}
                  </td>
                  <td style={cellStyle}></td>
                  <td style={cellStyle}></td>
                  <td style={cellStyle}></td>
                  <td style={cellStyle}></td>
                  <td style={boldCell}>ROUND OFF(-)</td>
                  <td style={cellStyle}>{roundOff.toFixed(2)}</td>
                </tr>
                <tr>
                  <td style={cellStyle}></td>
                  <td style={cellStyle}></td>
                  <td style={cellStyle}></td>
                  <td style={cellStyle}></td>
                  <td style={cellStyle}></td>
                  {/* One fewer filler cell keeps this row's rowspan-aware
                      column offset aligned when the GST column is hidden. */}
                  {gstApplicable && <td style={cellStyle}></td>}
                  <td style={boldCell}>GRAND TOTAL</td>
                  <td style={boldCell}>{formatAmount(grandTotal)}</td>
                </tr>
              </tbody>
            </table>

            {/* Bank details + signature */}
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr>
                  <td
                    style={{
                      ...cellStyle,
                      width: "50%",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {outlet?.outletBankDetails || ""}
                  </td>
                  <td style={{ ...cellStyle, width: "50%" }}></td>
                </tr>
                <tr>
                  <td style={{ ...cellStyle, paddingBottom: "2.2rem" }}>
                    CUSTOMER'S SIGNATURE
                  </td>
                  <td style={{ ...cellStyle, paddingBottom: "2.2rem" }}>
                    for {outlet?.outletName?.toUpperCase()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="justify-content-center">
        <Button
          className={`${userInfo?.themeColor}ThemeHeaderTop`}
          onClick={handleDownload}
          disabled={downloading}
        >
          {downloading ? "Downloading..." : "Download"}
        </Button>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InvoiceModal;
